import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, FormControlLabel, Checkbox, Button, Alert, List, ListItem, ListItemText, CircularProgress, Divider,
  Paper, Card, CardContent, Grid, Tooltip, LinearProgress, AppBar, Toolbar, IconButton, Fade, Zoom, ListItemIcon,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import InfoIcon from '@mui/icons-material/Info';
import { Chart as ChartJS, LineElement, PointElement, LinearScale, Title, CategoryScale, Tooltip as ChartTooltip, Legend, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';
import api from '../api';

ChartJS.register(LineElement, PointElement, LinearScale, Title, CategoryScale, ChartTooltip, Legend, Filler);

// Question pool with ~24 questions for variety, using tricky phrasing to promote honest answers.
// Sourced from trading psychology books with citations for educational value.
const allQuestionGroups = [
  {
    title: 'Mindset & Uncertainty',
    questions: [
      { id: 1, text: 'Would you not be surprised if the market does the opposite of what you expect today? (Embrace uncertainty without resistance.)', tip: 'Markets are random; accepting this reduces stress.', source: 'Trading in the Zone by Mark Douglas, Chapter 4: Accepting Uncertainty.' },
      { id: 2, text: 'Is there no chance you\'d break your trading rules even if a "perfect" setup appears? (Discipline is non-negotiable.)', tip: 'Rules as your edge—breaking them leads to emotional trading.', source: 'The Disciplined Trader by Mark Douglas, Chapter 7: Developing Discipline.' },
      { id: 3, text: 'Have you completely abandoned trying to predict or control where prices go? (Avoid illusion of control.)', tip: 'Illusion of control causes overconfidence; focus on probabilities.', source: 'Trading in the Zone by Mark Douglas, Chapter 5: The Illusion of Control.' },
      { id: 13, text: 'Do you accept that every trade has an uncertain outcome, regardless of analysis? (Probabilistic thinking.)', tip: 'Trades are probabilities, not certainties—reduces attachment.', source: 'Trading in the Zone by Mark Douglas, Chapter 3: Thinking in Probabilities.' },
      { id: 14, text: 'Are you comfortable with the idea that the market owes you nothing? (No entitlement mindset.)', tip: 'Entitlement leads to frustration; trade without expectations.', source: 'Come Into My Trading Room by Alexander Elder, Part 3: Psychology Section.' },
      { id: 19, text: 'Isn\'t it true that you wouldn\'t hesitate to exit a trade if it goes against you, without wishing for a turnaround? (No hope trading.)', tip: 'Hope is not a strategy—cut losses mechanically.', source: 'Market Wizards by Jack Schwager, Interviews on Discipline.' },
      { id: 20, text: 'Have you let go of the need to be right on every trade? (Ego detachment.)', tip: 'Being wrong is part of the game; focus on overall profitability.', source: 'The Psychology of Trading by Brett Steenbarger, Chapter 1: Ego and Trading.' },
      { id: 21, text: 'Wouldn\'t you remain calm even if three trades in a row lose? (Resilience to streaks.)', tip: 'Streaks are normal in probabilities—don\'t tilt.', source: 'Trading in the Zone by Mark Douglas, Chapter 8: Handling Losing Streaks.' },
    ],
  },
  {
    title: 'Emotions & Bias',
    questions: [
      { id: 4, text: 'Isn\'t fear of loss or greed for profits influencing your decisions at all today? (Manage emotions to stay objective.)', tip: 'Emotions distort perception—trade like a machine.', source: 'Trading in the Zone by Mark Douglas, Chapter 6: Managing Fear and Greed.' },
      { id: 9, text: 'Are you prioritizing the trading process over obsessing about potential profits? (Input over outcome.)', tip: 'Focus on execution; outcomes follow good processes.', source: 'The Disciplined Trader by Mark Douglas, Chapter 8: Process vs. Outcome.' },
      { id: 11, text: 'Won\'t you hope for or against any specific market direction, remaining fully neutral? (No bias.)', tip: 'Bias leads to confirmation errors—stay open-minded.', source: 'Trading Psychology 2.0 by Brett Steenbarger, Chapter 2: Avoiding Cognitive Biases.' },
      { id: 12, text: 'Aren\'t any regrets from yesterday\'s trades clouding your judgment now? (Emotional reset.)', tip: 'Reset daily; past trades are lessons, not baggage.', source: 'Come Into My Trading Room by Alexander Elder, Chapter 9: Emotional Discipline.' },
      { id: 15, text: 'Can you detach from the emotional high of wins and low of losses equally? (Emotional equilibrium.)', tip: 'Euphoria and despair both impair judgment—maintain balance.', source: 'Trading Psychology 2.0 by Brett Steenbarger, Chapter 4: Building Emotional Resilience.' },
      { id: 16, text: 'Are you free from confirmation bias, seeking evidence against your positions? (Objective analysis.)', tip: 'Actively disprove your thesis to avoid traps.', source: 'The Psychology of Trading by Brett Steenbarger, Chapter 3: Cognitive Biases in Trading.' },
      { id: 22, text: 'Isn\'t revenge trading something you\'d never consider after a loss? (No emotional escalation.)', tip: 'Revenge amplifies losses—pause and reassess.', source: 'High Probability Trading by Marcel Link, Chapter 5: Emotional Pitfalls.' },
      { id: 23, text: 'Do you avoid overconfidence after a winning streak? (Humility check.)', tip: 'Wins can breed hubris—stay grounded.', source: 'Fooled by Randomness by Nassim Taleb, Chapter on Overconfidence.' },
      { id: 24, text: 'Wouldn\'t you question your own analysis if it feels too "certain"? (Doubt as a tool.)', tip: 'Certainty is a red flag in uncertain markets.', source: 'Thinking, Fast and Slow by Daniel Kahneman, Applied to Trading Biases.' },
    ],
  },
  {
    title: 'Preparation & Discipline',
    questions: [
      { id: 5, text: 'Did you sleep well and feel sharp enough to handle fast market changes? (Physical readiness.)', tip: 'Fatigue amplifies errors—prioritize rest like pro athletes.', source: 'Come Into My Trading Room by Alexander Elder, Chapter 10: Trader\'s Health and Preparation.' },
      { id: 6, text: 'Is nothing in your personal life that could distract you during trading hours? (Clear mind.)', tip: 'Distractions lead to missed signals—create a trading sanctuary.', source: 'Trading in the Zone by Mark Douglas, Chapter 9: Creating a Focused Environment.' },
      { id: 7, text: 'After reviewing your journal, do you trust your strategy without second-guessing? (Confidence in edge.)', tip: 'Journaling builds trust—review wins/losses objectively.', source: 'The Disciplined Trader by Mark Douglas, Chapter 6: Journaling for Confidence.' },
      { id: 8, text: 'Would you embrace small losses as normal without any frustration? (Preserve capital.)', tip: 'Losses are business costs—accept them to protect capital.', source: 'Trading in the Zone by Mark Douglas, Chapter 7: Accepting Losses.' },
      { id: 10, text: 'Will you strictly avoid trading more than your daily limit, no matter how tempting? (Discipline on overtrading.)', tip: 'Overtrading erodes edges—set and honor limits.', source: 'Come Into My Trading Room by Alexander Elder, Chapter 11: Risk Management and Limits.' },
      { id: 17, text: 'Have you prepared a detailed trading plan for today, including exit strategies? (Structured approach.)', tip: 'Plans reduce impulsivity—stick to predefined rules.', source: 'Trading Psychology 2.0 by Brett Steenbarger, Chapter 5: Planning and Preparation.' },
      { id: 18, text: 'Are you committed to stopping trading if you hit your daily loss limit? (Self-control.)', tip: 'Loss limits prevent revenge trading—enforce them rigidly.', source: 'The Disciplined Trader by Mark Douglas, Chapter 10: Enforcing Limits.' },
      { id: 25, text: 'Isn\'t multitasking during trading sessions something you\'ve eliminated? (Focused execution.)', tip: 'Multitasking divides attention—trade in flow state.', source: 'Flow: The Psychology of Optimal Experience by Mihaly Csikszentmihalyi, Applied to Trading.' },
      { id: 26, text: 'Do you review potential trades without attachment to executing them? (Selective discipline.)', tip: 'Not every setup is worth taking—wait for high-probability.', source: 'High Probability Trading by Marcel Link, Chapter 3: Selective Trading.' },
    ],
  },
];

// Function to get daily offset for cycling rotation, adjusted to South African time (SAST, UTC+2).
const getDailyOffset = () => {
  const sastDate = new Date(new Date().toLocaleString('en-US', { timeZone: 'Africa/Johannesburg' }));
  const dayOfYear = Math.floor((sastDate - new Date(sastDate.getFullYear(), 0, 1)) / (1000 * 60 * 60 * 24));
  return dayOfYear;
};

// Cycle through questions using SAST-based offset. Select 4 per group, wrapping around.
const getDailyQuestions = (groups) => {
  const offset = getDailyOffset();
  return groups.map(group => {
    const len = group.questions.length;
    const start = offset % len;
    const selected = [];
    for (let i = 0; i < 4; i++) {
      selected.push(group.questions[(start + i) % len]);
    }
    return { ...group, questions: selected };
  });
};

const motivationalQuotes = [
  '"The best traders have no ego. They trade without expectations." - Mark Douglas',
  '"Discipline is the bridge between goals and accomplishment." - Jim Rohn (inspired by trading psychology)',
  '"Success in trading is 80% psychology, 20% mechanics." - Alexander Elder',
  '"The market is a device for transferring money from the impatient to the patient." - Warren Buffett (trading adaptation)',
  '"Trade what you see, not what you think." - Anonymous (from psychology forums)',
  '"Honesty in self-assessment is the trader\'s greatest edge." - Adapted from Mark Douglas',
];

const TraderAnalysis = () => {
  const navigate = useNavigate();
  const [answers, setAnswers] = useState(() => {
    const saved = localStorage.getItem('psychAnswers');
    return saved ? JSON.parse(saved) : {};
  });
  const [submitted, setSubmitted] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [score, setScore] = useState(0); // eslint-disable-line no-unused-vars
  const [analysis, setAnalysis] = useState('');
  const [tips, setTips] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState(motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);

  // Daily questions using SAST-based cycling rotation
  const dailyQuestionGroups = useMemo(() => getDailyQuestions(allQuestionGroups), []);

  // Randomize order within each group for variety
  const randomizedDailyGroups = useMemo(() => {
    return dailyQuestionGroups.map(group => ({
      ...group,
      questions: [...group.questions].sort(() => Math.random() - 0.5),
    }));
  }, [dailyQuestionGroups]);

  // Initialize answers for daily questions
  useEffect(() => {
    const flatQuestions = randomizedDailyGroups.flatMap(g => g.questions);
    setAnswers(prev => {
      const updated = { ...prev };
      flatQuestions.forEach(q => {
        if (!(q.id in updated)) updated[q.id] = false;
      });
      return updated;
    });
  }, [randomizedDailyGroups]);

  const handleChange = useCallback((id) => {
    setAnswers((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        console.log('🔍 Fetching psychology history...');
        const res = await api.get('/api/psychology/checks');
        console.log('✅ Psychology history response:', res.data);
        const sortedHistory = res.data.sort((a, b) => new Date(a.date) - new Date(b.date));
        console.log('📅 Sorted history:', sortedHistory);
        setHistory(sortedHistory);
      } catch (err) {
        console.error('❌ Failed to load psychology history:', err);
        if (err.response?.status === 401) {
          setError('Authentication failed. Please refresh the page and try again.');
        } else {
          setError('Failed to load history: ' + (err.response?.data?.message || err.message));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  useEffect(() => {
    localStorage.setItem('psychAnswers', JSON.stringify(answers));
  }, [answers]);

  const progress = useMemo(() => {
    const total = randomizedDailyGroups.flatMap(g => g.questions).length;
    const answered = Object.values(answers).filter(Boolean).length;
    return (answered / total) * 100;
  }, [answers, randomizedDailyGroups]);

  const todayCheck = useMemo(() => {
    // Fix: Get the correct SAST date using proper timezone conversion
    const now = new Date();
    const sastOptions = { 
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    };
    const sastDateString = now.toLocaleDateString('en-CA', sastOptions); // en-CA gives YYYY-MM-DD format
    
    console.log('🔍 TodayCheck Debug:', {
      sastDateString,
      utcDate: now.toISOString().split('T')[0],
      historyDates: history.map(item => item.date),
      historyLength: history.length
    });
    
    const found = history.find((item) => item.date === sastDateString);
    console.log('✅ TodayCheck Result:', found);
    
    return found;
  }, [history]);

  const calculateResults = useCallback(() => {
    const flatQuestions = randomizedDailyGroups.flatMap(g => g.questions);
    const yesCount = flatQuestions.filter(q => answers[q.id]).length;
    const total = flatQuestions.length;
    const newScore = (yesCount / total) * 100;
    const isEligible = newScore >= 80;

    let newAnalysis = `Score: ${newScore.toFixed(0)}% (${yesCount}/${total} Yes answers).\n`;
    let newTips = [];
    let reasons = [];
    if (isEligible) {
      newAnalysis += 'Eligible to trade! Your mindset is strong—aligned with key psychology principles. Keep embracing uncertainty.';
      if (newScore === 100) {
        newAnalysis += '\nPerfect score—impressive if honest! But reflect: Are you truly this prepared, or overriding doubts? Honesty builds real edges.';
      }
      newTips.push('Daily Tip: Start with 5-min meditation to maintain focus.');
    } else {
      newAnalysis += 'Not eligible yet. Focus on these gaps:\n';
      flatQuestions.forEach((q) => {
        if (!answers[q.id]) {
          reasons.push(q);
          newAnalysis += `- ${q.text} (No): Gap in ${q.tip.split('.')[0]}. Source: ${q.source}\n`;
          newTips.push(`Improve: ${q.tip} Action: Journal why you said no and plan a fix. Read: ${q.source}`);
        }
      });
      newAnalysis += 'Recommendation: Review cited chapters. Retry tomorrow. Remember, faking answers only risks your capital—trade smart.';
      newTips.push('Daily Tip: Use breathing exercises (4-7-8 technique) to reset emotions.');
    }

    return { score: newScore, eligible: isEligible, analysis: newAnalysis, tips: newTips, reasons };
  }, [answers, randomizedDailyGroups]);

  const handleSubmit = async () => {
    if (todayCheck) {
      setError('You have already submitted a psychology check for today. Try again tomorrow.');
      return;
    }

    const { score: newScore, eligible: isEligible, analysis: newAnalysis, tips: newTips, reasons } = calculateResults();
    setScore(newScore);
    setEligible(isEligible);
    setAnalysis(newAnalysis);
    setTips(newTips);
    setSubmitted(true);

    try {
      const now = new Date();
      const sastOptions = { 
        timeZone: 'Africa/Johannesburg',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      };
      const today = now.toLocaleDateString('en-CA', sastOptions);
      const payload = { date: today, answers, score: newScore, eligible: isEligible, analysis: newAnalysis, reasons };
      console.log('Submitting psychology check payload:', payload);
      
      await api.post('/api/psychology/check', payload);
      const res = await api.get('/api/psychology/checks');
      const sortedHistory = res.data.sort((a, b) => new Date(a.date) - new Date(b.date));
      setHistory(sortedHistory);
      localStorage.removeItem('psychAnswers');
      setError('');
    } catch (err) {
      console.error('Failed to save psychology check:', err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please refresh the page and try again.');
      } else {
        setError('Failed to save check: ' + (err.response?.data?.message || err.message));
      }
    }
  };

  const chartData = {
    labels: history.map(item => item.date),
    datasets: [{
      label: 'Psychology Score Trend (%)',
      data: history.map(item => item.score),
      borderColor: '#1976d2',
      backgroundColor: 'rgba(25, 118, 210, 0.2)',
      fill: true,
      tension: 0.4,
    }],
  };

  const handleViewDetails = (item) => {
    setSelectedHistoryItem(item);
  };

  const handleCloseModal = () => {
    setSelectedHistoryItem(null);
  };

  if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'linear-gradient(to bottom, #f5f5f5, #e0e0e0)', p: 4 }}>
      <AppBar position="sticky" sx={{ bgcolor: 'var(--primary-color)', mb: 4 }}>
        <Toolbar>
          <Tooltip title="Back to Dashboard">
            <IconButton color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            Trader Psychology Check
          </Typography>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} sx={{ maxWidth: 900, mx: 'auto', p: 4, borderRadius: 4, bgcolor: '#fff' }}>
        <Fade in={true} timeout={800}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', color: '#1976d2' }}>
              Daily Trader Psychology Check
            </Typography>
            <Typography variant="body1" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
              {quote} — Be brutally honest with yourself. These questions are phrased trickily to probe your true mindset—faking "yes" to game the system only sets you up for real-market failures. This is for your growth, not judgment. Time: {new Date().toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg' })}.
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ mb: 4, height: 8, borderRadius: 4 }} />
          </Box>
        </Fade>

        {todayCheck ? (
          <Alert severity="info" sx={{ mb: 4 }}>
            You've already completed today's psychology check. Score: {todayCheck.score.toFixed(0)}% (
            {todayCheck.eligible ? 'Eligible' : 'Not Eligible'}). Try again tomorrow.
            <Box sx={{ mt: 2 }}>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => {
                  console.log('🔄 Manually refreshing history...');
                  window.location.reload();
                }}
              >
                Refresh Page
              </Button>
            </Box>
          </Alert>
        ) : !submitted ? (
          <Grid container spacing={3}>
            {randomizedDailyGroups.map((group, idx) => (
              <Grid item xs={12} key={idx}>
                <Zoom in={true} timeout={500 * (idx + 1)}>
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, color: '#dc004e' }}>{group.title}</Typography>
                      {group.questions.map((q) => (
                        <Tooltip key={q.id} title={`${q.tip} Source: ${q.source}`} arrow>
                          <FormControlLabel
                            control={<Checkbox checked={answers[q.id]} onChange={() => handleChange(q.id)} size="medium" />}
                            label={q.text}
                            sx={{ display: 'block', mb: 1, '& .MuiFormControlLabel-label': { fontSize: '1rem' } }}
                          />
                        </Tooltip>
                      ))}
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Button variant="contained" color="primary" onClick={handleSubmit} sx={{ mt: 2, px: 4 }}>
                Submit & Get Analysis
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Fade in={true} timeout={800}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                {eligible ? (
                  <CheckCircleIcon color="success" sx={{ fontSize: 50, mr: 2 }} />
                ) : (
                  <CancelIcon color="error" sx={{ fontSize: 50, mr: 2 }} />
                )}
                <Typography variant="h5" sx={{ color: eligible ? 'green' : 'red' }}>
                  {eligible ? 'Eligible to Trade Today!' : 'Not Eligible—Focus on Improvements'}
                </Typography>
              </Box>
              <Alert severity={eligible ? 'success' : 'warning'} sx={{ whiteSpace: 'pre-wrap', mb: 4 }}>
                {analysis}
              </Alert>
              <Typography variant="h6" gutterBottom>Daily Success Plan</Typography>
              <List>
                {tips.map((tip, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon><LightbulbIcon color="primary" /></ListItemIcon>
                    <ListItemText primary={tip} />
                  </ListItem>
                ))}
              </List>
              <Button variant="outlined" onClick={() => { setSubmitted(false); setQuote(motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)]); }} sx={{ mt: 2, mr: 2 }}>
                Retake Assessment
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/consequences-report/${new Date().toISOString().split('T')[0]}`)}
                sx={{ mt: 2 }}
              >
                View Detailed Report
              </Button>
            </Box>
          </Fade>
        )}

        {error && <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>}

        <Divider sx={{ my: 4 }} />
        <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>Performance History & Trend</Typography>
        {history.length === 0 ? (
          <Typography sx={{ textAlign: 'center' }}>No history yet. Complete your first check!</Typography>
        ) : (
          <>
            <Box sx={{ height: 300, mb: 4 }}>
              <Line data={chartData} options={{ responsive: true, scales: { y: { min: 0, max: 100 } } }} />
            </Box>
            <List>
              {history.map((item) => (
                <ListItem key={item.date} button onClick={() => handleViewDetails(item)}>
                  <ListItemText
                    primary={`${item.date}: ${item.eligible ? 'Eligible' : 'Not Eligible'} (Score: ${item.score.toFixed(0)}%)`}
                    secondary={item.analysis.substring(0, 100) + '...'}
                  />
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Paper>

      <Dialog open={!!selectedHistoryItem} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>History Details: {selectedHistoryItem?.date}</DialogTitle>
        <DialogContent>
          <Typography variant="h6">Score: {selectedHistoryItem?.score.toFixed(0)}% ({selectedHistoryItem?.eligible ? 'Eligible' : 'Not Eligible'})</Typography>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 2 }}>
            {selectedHistoryItem?.analysis}
          </Typography>
          {!selectedHistoryItem?.eligible && selectedHistoryItem?.reasons && (
            <>
              <Typography variant="h6" sx={{ mt: 3 }}>Reasons Why You Couldn't Trade:</Typography>
              <List>
                {selectedHistoryItem.reasons.map((reason, idx) => (
                  <ListItem key={idx}>
                    <ListItemText
                      primary={reason.text}
                      secondary={`Tip: ${reason.tip} Source: ${reason.source}`}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => navigate(`/consequences-report/${selectedHistoryItem?.date}`)}
            color="primary"
            variant="contained"
            sx={{ mr: 2 }}
          >
            View Detailed Report
          </Button>
          <Button onClick={handleCloseModal} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TraderAnalysis;
